import { useEffect, useState } from 'react';

const BASE_CURRENCY = 'DKK';
const TARGET_CURRENCIES = ['USD', 'EUR'];
const REFRESH_MS = 60 * 60 * 1000;
const RATES_URL = `https://api.frankfurter.dev/v1/latest?from=${BASE_CURRENCY}&to=${TARGET_CURRENCIES.join(',')}`;

async function fetchRates() {
  const response = await fetch(RATES_URL);

  if (!response.ok) {
    throw new Error('Could not load exchange rates');
  }

  const payload = await response.json();
  const nextRates = {
    [BASE_CURRENCY]: 1,
    ...(payload.rates || {}),
  };

  if (!nextRates.USD || !nextRates.EUR) {
    throw new Error('Exchange rates response was incomplete');
  }

  return nextRates;
}

async function fetchRatesWithRetry(attempts = 3) {
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fetchRates();
    } catch (err) {
      lastError = err;
      if (attempt < attempts - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  }

  throw lastError || new Error('Could not load exchange rates');
}

export function useExchangeRates() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRates() {
      setLoading(true);
      try {
        const nextRates = await fetchRatesWithRetry();
        if (!cancelled) {
          setRates(nextRates);
          setUpdatedAt(new Date().toISOString());
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Exchange rates unavailable');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRates();
    const intervalId = window.setInterval(loadRates, REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return { rates, loading, error, updatedAt, baseCurrency: BASE_CURRENCY };
}
